import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import path from 'path';
import { guid } from '@/mixins/guid';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';

export default Vue.component('image-option', {
  template: require('./template.html'),
  name: 'image-option',
  mixins: [guid, uploader],
  components: {
    uploadProgress,
  },
  computed: {
    ...mapGetters(['viewport', 'activeDroplet', 'activeDropletItem']),
    sectionRule() {
      return _.find(this.activeDroplet.mediaQueries, { selector: ' ' });
    },
  },
  props: [
    'option',
    'active-state',
    'dom-nodes',
    'bg-type',
    'component-config',
    'default-values',
  ],
  data() {
    return {
      debTime: 250,
      showConfirm: false,
      showFiles: false,
      loading: false,
      imageFiles: [],
      hoverDel: false,
      activeImg: null,
      activeIndex: null,
      innerDomNodes: {},
      prefix: '',
      bucketPath: '',
      thumbnails: '',
      cacheBgImg: '',
      display: {
        bgImg: '',
        size: null,
        repeat: null,
        position: null,
        attachment: null,
      },
      bgProperties: [
        {
          label: 'Background Size',
          rule: 'size',
          options: [
            { label: 'Contain', value: 'contain' },
            { label: 'Cover', value: 'cover' },
            { label: 'Auto', value: 'auto' },
          ],
        },
        {
          label: 'Background Repeat',
          rule: 'repeat',
          options: [
            { label: 'No Repeat', value: 'no-repeat' },
            { label: 'Full Repeat', value: 'repeat' },
            { label: 'Repeat Left to Right', value: 'repeat-x' },
            { label: 'Repeat Top to Bottom', value: 'repeat-y' },
          ],
        },
        {
          label: 'Background Position',
          rule: 'position',
          options: [
            { label: 'Center Center', value: 'center center' },
            { label: 'Center Top', value: 'center top' },
            { label: 'Center Bottom', value: 'center bottom' },
            { label: 'Left Center', value: 'left center' },
            { label: 'Left Top', value: 'left top' },
            { label: 'Left Bottom', value: 'left bottom' },
            { label: 'Right Center', value: 'right center' },
            { label: 'Right Top', value: 'right top' },
            { label: 'Right Bottom', value: 'right bottom' },
          ],
        },
        {
          label: 'Background Attachment',
          rule: 'attachment',
          options: [
            { label: 'Scroll', value: 'scroll' },
            { label: 'Fixed', value: 'fixed' },
            { label: 'Local', value: 'local' },
            { label: 'Unset', value: 'unset' },
          ],
        },
      ],
    };
  },
  watch: {
    viewport() {
      this.setInitialValue();
    },
    activeState() {
      this.setInitialValue();
    },
    activeGroupItem(item) {
      if (item) {
        this.setEditValues(item);
      }
    },
    'pickerVals.editor': function initPickerVal(active) {
      if (active) {
        this.setAngleSlider();
      }
    },
    activeDropletItem: {
      handler() {
        this.setInitialValue();
      },
      deep: true,
    },
  },
  created() {
    _.forOwn(this.option.payload.models, (value, key) => {
      this[key] = value;
    });
  },
  mounted() {
    this.setInitialValue();
  },
  methods: {
    setInitialValue() {
      const properties = this.getBgImg();
      this.display.bgImg = properties.file;
      // grab all background property rules
      _.forEach(this.bgProperties, (item) => {
        this.display[item.rule] = properties[item.rule];
      });
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    async uploadBg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          let newFile = [...e.target.files];
          if (newFile.length > 0) {
            newFile = newFile[0];
            // clean up the file name for s3
            let fileName = path.basename(newFile.name);
            fileName = `${this.prefix}${_.trim(fileName)}`;
            fileName = _.replace(fileName, /\s+/g, '_');
            try {
              // send off to our s3 upload mixin
              const filePath = `/${this.bucketPath}${fileName}`;
              await this.uploadFile({
                file: `${this._sitePath}${filePath}`,
                body: newFile,
                thumbnail: {
                  width: 35,
                  srcFile: newFile,
                  name: fileName,
                  bucketPath: `${this._sitePath}/${this.thumbnails}`,
                },
              });
              await this.setSectionBgImg(filePath);
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'section uploadBg',
                });
              }
            } finally {
              if (this.showFiles) {
                setTimeout(() => {
                  this.fetchImageFiles();
                }, 500);
              }
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    buildValue() {
      const data = this.display;
      let attachment = data.attachment;
      if (attachment === 'unset' || typeof attachment === 'undefined') {
        attachment = '';
      }
      return `${data.position} / ${data.size} ${data.repeat} ${attachment} url(${data.bgImg})`;
    },
    onChange(params) {
      this.$emit('update-styles', {
        ...params,
        imageVal: this.buildValue(),
      });
    },
    async setSectionBgImg(f, url) {
      // if came from click, gather full url
      let file = f;
      if (f) {
        file = `/${this.bucketPath}${this._getFromThumb(path.basename(f))}`;
      } else {
        file = url;
      }
      this.display.bgImg = file;
      this.onChange(this.option.payload);
    },
    toggleFileBrowser() {
      this.showFiles = !this.showFiles;
      if (this.showFiles) {
        this.fetchImageFiles();
      }
    },
    async fetchImageFiles() {
      try {
        this.loading = true;
        const assetsFolder = '/_droplet-assets';
        const files = await this._s3fetchFiles(`${this._sitePath}/${this.thumbnails}`);
        const imagesOnly = files.filter(f => !['.mov', '.mp4'].includes(path.extname(this._getFromThumb(f))));
        this.imageFiles = imagesOnly.map(f => `${assetsFolder}${f.split(assetsFolder)[1]}`);
      } catch (err) {
        this.$log.error('error: ', err);
        this.showFiles = false;
        await this._reportError({
          error: err,
          fn: 'fetchImageFiles',
        });
      } finally {
        this.loading = false;
      }
    },
    async deleteImg() {
      // prep notifcation type
      const notif = {
        id: this.guid(),
      };
      const file = path.basename(this._getFromThumb(this.activeImg));
      const thumb = path.basename(this.activeImg);
      try {
        this.loading = true;
        // check session first
        await this._cognitoCheckSession();
        // delete original from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
        });
        // delete thumbnail from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
        });
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted image: ${file}`;
      } catch (err) {
        this.$log.error('error: ', err);
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: 'deleteImg',
        });
      } finally {
        // send off to notifications
        this.$store.dispatch('addNotification', notif);
        // kill loader & refetch files
        this.loading = false;
        this.showConfirm = false;
        this.fetchImageFiles();
      }

      // if deleting image that is being used, reset any viewports using it to default
      const deleted = `/${this.bucketPath}${file}`;
      if (deleted === this.display.bgImg) {
        this.setSectionBgImg(false, this.defaultValues.imageFile);
      }
    },
    getBgImg() {
      const option = _.cloneDeep(this.option);
      const first = option.payload.updates[0];
      let rule = first;
      let groupParent;
      if (this.areaChecked) {
        const checked = _.keys(_.pickBy(this.areas));
        rule = _.find(option.payload.updates, update => checked.includes(update.property));
      }
      let selector;
      if (this.activeDropletItem._id && this.componentConfig.itemList) {
        groupParent = first;
        const inner = this.componentConfig.itemList.selectors[rule.property];
        const nested = inner || '';
        selector = `#drp-${this.activeDropletItem._id}${nested}${this.activeState}`;
      } else {
        selector = rule.selector.replace(/\$\{state\}/gi, this.activeState);
        selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
      }
      const property = rule.property;
      // if one area is checked, get that rule from the updates list
      const useRoot = option.$el === 'root';
      const $root = this.domNodes.$dropletRow;
      const $el = useRoot ? $root : $root.querySelectorAll(option.$el)[0];
      const string = this._getPropertyRule({
        $vm: this,
        default: this.defaultValues.imageFile,
        $el,
        selector,
        property,
        groupParent,
      });
      const original = string;
      const url = new RegExp(/url\(([^)]+)\)/, 'gi');
      // break out properties for bg rules
      const props = original.replace(url, '');
      const splitProps = props.split('/');
      const others = splitProps[1];
      const othersSplit = others.trim().split(' ');
      // actual values
      const position = splitProps[0].trim();
      const size = othersSplit[0];
      const repeat = othersSplit[1];
      const attachment = othersSplit[2] || 'unset';
      // get url of media file
      const mediaString = string.match(url)[0];
      const file = mediaString.replace(/url\('|'\)|url\(|\)/gi, '');
      return {
        file,
        position,
        size,
        repeat,
        attachment,
      };
    },
    matchBg(image) {
      let display = this.display.bgImg.replace('url(', '');
      display = display.replace('\')', '');
      display = display.split('/').pop();
      const img = this._getFromThumb(path.basename(image));
      return img === display;
    },
    confirmDelete(file) {
      this.activeImg = file;
      this.toggleConfirm();
    },
    toggleConfirm() {
      this.showConfirm = !this.showConfirm;
    },
    getFileName() {
      return path.basename(this.display.bgImg);
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/options/background-options/image/index.js